import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumJourneyStopPlanningStatus } from "./enums/enumJourneyStopPlanningStatus.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class JourneyVehicleCheckLineReadOnly extends ModelObject<JourneyVehicleCheckLineReadOnly> {

    /**
    * Journey Header Id
    */
    public journeyId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.journeyId = Guid.parseOrEmpty(jsonSerializedObject.journeyId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.journeyId = this.journeyId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "journeyId", rule: (v: any) => !v.isEmpty() || "JourneyVehicleCheckLineReadOnly.JourneyId cannot be empty"},
    ];
}


export class JourneyVehicleCheckLine extends ModelObject<JourneyVehicleCheckLine> {

    /**
    * The check which was asked.
    */
    public vehicleCheckId: Guid = Guid.createEmpty();
    /**
    * Answer, in Text format. Even questions with well-typed AnswerFormat will have their answers stored textually.
    * Not Nullable
    * Max Length : 1000
    */
    public answer: string = "";
    public readOnly: JourneyVehicleCheckLineReadOnly = new JourneyVehicleCheckLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vehicleCheckId = Guid.parseOrEmpty(jsonSerializedObject.vehicleCheckId);
          this.answer = jsonSerializedObject.answer;
          this.readOnly = new JourneyVehicleCheckLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vehicleCheckId = this.vehicleCheckId.toString();
        toRet.answer = this.answer;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vehicleCheckId", rule: (v: any) => !v.isEmpty() || "JourneyVehicleCheckLine.VehicleCheckId cannot be empty"},
            {field: "answer", rule: (v: any) => v == null || v.length <= 1000 || "Answer has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class JourneyHeaderReadOnly extends ModelObject<JourneyHeaderReadOnly> {

    /**
    * Indicates if the driver has checked and confirmed the vehicle is fit for purpose prior to departure.
    */
    public confirmedVehicleFitForPurpose: boolean = false;
    private _endedAt: Instant | null = null;
    /**
    * Timestamp of when the journey was ended.
    */
    public get endedAt(): Instant | null { return this._endedAt }
    public set endedAt(value: Instant | null) {
      this._endedAt = value === undefined ? null : value;
    }
    private _endedBy: Guid | null = null;
    /**
    * Id of the user who ended the journey.
    */
    public get endedBy(): Guid | null { return this._endedBy }
    public set endedBy(value: Guid | null) {
      this._endedBy = value === undefined ? null : value;
    }
    private _endOdometerReading: number | null = null;
    /**
    * The vehicle odometer reading at the end of the journey. Must be provided before the journey can end.
    */
    public get endOdometerReading(): number | null { return this._endOdometerReading }
    public set endOdometerReading(value: number | null) {
      this._endOdometerReading = value === undefined ? null : value;
    }
    /**
    * Indicates if the regular vehicle monthly checks were performed prior to departure.
    */
    public monthlyChecksPerformed: boolean = false;
    private _startedAt: Instant | null = null;
    /**
    * Timestamp of when the journey was started. Presence of a value implies journey has been started and is therefore locked unless it has also been ended.
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    private _startedByUserId: Guid | null = null;
    /**
    * User who has started the journey. Journey is locked to the user until the journey is ended.
    */
    public get startedByUserId(): Guid | null { return this._startedByUserId }
    public set startedByUserId(value: Guid | null) {
      this._startedByUserId = value === undefined ? null : value;
    }
    private _startOdometerReading: number | null = null;
    /**
    * The vehicle odometer reading at the start of the journey. Must be provided before the journey can end.
    */
    public get startOdometerReading(): number | null { return this._startOdometerReading }
    public set startOdometerReading(value: number | null) {
      this._startOdometerReading = value === undefined ? null : value;
    }
    /**
    * Indicates if the regular vehicle weekly checks were performed prior to departure.
    */
    public weeklyChecksPerformed: boolean = false;
    /**
    * Indicates if the loading list was checked prior to departure.
    */
    public loadingListChecked: boolean = false;
    /**
    * The sequence of this journey within the plan for the day of the vehicle.
    */
    public sequence: number = 0;
    public vehicleCheckLines: (JourneyVehicleCheckLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.confirmedVehicleFitForPurpose = jsonSerializedObject.confirmedVehicleFitForPurpose;
          this.endedAt = jsonSerializedObject.endedAt == null ? null : Instant.parse(jsonSerializedObject.endedAt);
          this.endedBy = jsonSerializedObject.endedBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.endedBy);
          this.endOdometerReading = jsonSerializedObject.endOdometerReading == null ? null : jsonSerializedObject.endOdometerReading;
          this.monthlyChecksPerformed = jsonSerializedObject.monthlyChecksPerformed;
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.startedByUserId = jsonSerializedObject.startedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.startedByUserId);
          this.startOdometerReading = jsonSerializedObject.startOdometerReading == null ? null : jsonSerializedObject.startOdometerReading;
          this.weeklyChecksPerformed = jsonSerializedObject.weeklyChecksPerformed;
          this.loadingListChecked = jsonSerializedObject.loadingListChecked;
          this.sequence = jsonSerializedObject.sequence;
          this.vehicleCheckLines = jsonSerializedObject.vehicleCheckLines.map((v: any) => new JourneyVehicleCheckLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.confirmedVehicleFitForPurpose = this.confirmedVehicleFitForPurpose;
        toRet.endedAt = this.endedAt == null ? null : this.endedAt.toString();
        toRet.endedBy = this.endedBy == null ? null : this.endedBy.toString();
        toRet.endOdometerReading = this.endOdometerReading == null ? null : this.endOdometerReading;
        toRet.monthlyChecksPerformed = this.monthlyChecksPerformed;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.startedByUserId = this.startedByUserId == null ? null : this.startedByUserId.toString();
        toRet.startOdometerReading = this.startOdometerReading == null ? null : this.startOdometerReading;
        toRet.weeklyChecksPerformed = this.weeklyChecksPerformed;
        toRet.loadingListChecked = this.loadingListChecked;
        toRet.sequence = this.sequence;
        toRet.vehicleCheckLines = this.vehicleCheckLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "endedBy", rule: (v: any) => v == null || !v.isEmpty() || "JourneyHeaderReadOnly.EndedBy cannot be empty"},
            {field: "startedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyHeaderReadOnly.StartedByUserId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "vehicleCheckLines", rule: (v: any) => !!v || "vehicleCheckLines is required"},
            {field: "vehicleCheckLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as JourneyVehicleCheckLine[]).reduce((acc: Array<string>, curr: JourneyVehicleCheckLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneyHeader extends ModelObject<JourneyHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Vehicle Id
    */
    public vehicleId: Guid = Guid.createEmpty();
    /**
    * Date of Despatch
    */
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The expected duration the journey will take.
    * Not Negative
    */
    public expectedDurationMinutes: number = 0;
    /**
    * The distribution centre this journey departs from.
    */
    public siteId: Guid = Guid.createEmpty();
    /**
    * Driver Name
    * Not Nullable
    * Max Length : 1000
    */
    public driverName: string = "";
    /**
    * The approximate distance of the journey in metres.
    * Not Negative
    */
    public approximateDistanceMetres: number = 0;
    public readOnly: JourneyHeaderReadOnly = new JourneyHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.vehicleId = Guid.parseOrEmpty(jsonSerializedObject.vehicleId);
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.expectedDurationMinutes = jsonSerializedObject.expectedDurationMinutes;
          this.siteId = Guid.parseOrEmpty(jsonSerializedObject.siteId);
          this.driverName = jsonSerializedObject.driverName;
          this.approximateDistanceMetres = jsonSerializedObject.approximateDistanceMetres;
          this.readOnly = new JourneyHeaderReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.vehicleId = this.vehicleId.toString();
        toRet.despatchDate = this.despatchDate.toString();
        toRet.expectedDurationMinutes = this.expectedDurationMinutes;
        toRet.siteId = this.siteId.toString();
        toRet.driverName = this.driverName;
        toRet.approximateDistanceMetres = this.approximateDistanceMetres;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "JourneyHeader.Id cannot be empty"},
            {field: "vehicleId", rule: (v: any) => !v.isEmpty() || "JourneyHeader.VehicleId cannot be empty"},
            {field: "expectedDurationMinutes", rule: (v: any) => v == null || v >= 0 || "ExpectedDurationMinutes is not greater than minimum allowed value: 0"},
            {field: "expectedDurationMinutes", rule: (v: any) => v == null || v <= 2147483647 || "ExpectedDurationMinutes is above maximum allowed value: 2147483647"},
            {field: "siteId", rule: (v: any) => !v.isEmpty() || "JourneyHeader.SiteId cannot be empty"},
            {field: "driverName", rule: (v: any) => v == null || v.length <= 1000 || "DriverName has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class StopKey extends ModelObject<StopKey> {

    private _orderId: Guid | null = null;
    /**
    * Order Id
    */
    public get orderId(): Guid | null { return this._orderId }
    public set orderId(value: Guid | null) {
      this._orderId = value === undefined ? null : value;
    }
    private _ullageId: Guid | null = null;
    /**
    * Ullage Id
    */
    public get ullageId(): Guid | null { return this._ullageId }
    public set ullageId(value: Guid | null) {
      this._ullageId = value === undefined ? null : value;
    }
    private _visitId: Guid | null = null;
    /**
    * Visit Id
    */
    public get visitId(): Guid | null { return this._visitId }
    public set visitId(value: Guid | null) {
      this._visitId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderId = jsonSerializedObject.orderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.ullageId = jsonSerializedObject.ullageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.ullageId);
          this.visitId = jsonSerializedObject.visitId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.visitId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderId = this.orderId == null ? null : this.orderId.toString();
        toRet.ullageId = this.ullageId == null ? null : this.ullageId.toString();
        toRet.visitId = this.visitId == null ? null : this.visitId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "orderId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.OrderId cannot be empty"},
            {field: "ullageId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.UllageId cannot be empty"},
            {field: "visitId", rule: (v: any) => v == null || !v.isEmpty() || "StopKey.VisitId cannot be empty"},
    ];
}


export class Journey extends ModelObject<Journey> {

    public header: JourneyHeader = new JourneyHeader();
    /**
    * Ordered list of stop keys on this journey.
    */
    public stopKeys: (StopKey)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new JourneyHeader(jsonSerializedObject.header);
          this.stopKeys = jsonSerializedObject.stopKeys.map((v: any) => new StopKey(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.stopKeys = this.stopKeys.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "stopKeys", rule: (v: any) => !!v || "stopKeys is required"},
            {field: "stopKeys", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StopKey[]).reduce((acc: Array<string>, curr: StopKey) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneyFilter extends ModelObject<JourneyFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromDespatchDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) despatch date
    */
    public get fromDespatchDate(): LocalDate | null { return this._fromDespatchDate }
    public set fromDespatchDate(value: LocalDate | null) {
      this._fromDespatchDate = value === undefined ? null : value;
    }
    private _toDespatchDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) despatch date
    */
    public get toDespatchDate(): LocalDate | null { return this._toDespatchDate }
    public set toDespatchDate(value: LocalDate | null) {
      this._toDespatchDate = value === undefined ? null : value;
    }
    /**
    * Yes = Journeys which have started only (they may have completed too), No = Not Started Only, Either = Either
    */
    public startedOrNot: EnumYesNoEither = EnumYesNoEither.either;
    /**
    * Yes = Journeys which have ended only, No = Not Ended Only, Either = Either
    */
    public endedOrNot: EnumYesNoEither = EnumYesNoEither.either;
    private _vehicleIds: readonly (Guid)[] | null = null;
    public get vehicleIds(): readonly (Guid)[] | null { return this._vehicleIds }
    public set vehicleIds(value: readonly (Guid)[] | null) {
      this._vehicleIds = value === undefined ? null : value;
    }
    private _journeyIds: readonly (Guid)[] | null = null;
    /**
    * Filter to only journeys matching any given ids
    */
    public get journeyIds(): readonly (Guid)[] | null { return this._journeyIds }
    public set journeyIds(value: readonly (Guid)[] | null) {
      this._journeyIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromDespatchDate = jsonSerializedObject.fromDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDespatchDate);
          this.toDespatchDate = jsonSerializedObject.toDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.toDespatchDate);
          this.startedOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.startedOrNot, this.startedOrNot);
          this.endedOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.endedOrNot, this.endedOrNot);
          this.vehicleIds = jsonSerializedObject.vehicleIds == null ? null : jsonSerializedObject.vehicleIds.map((v: any) => Guid.parseOrEmpty(v));
          this.journeyIds = jsonSerializedObject.journeyIds == null ? null : jsonSerializedObject.journeyIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromDespatchDate = this.fromDespatchDate == null ? null : this.fromDespatchDate.toString();
        toRet.toDespatchDate = this.toDespatchDate == null ? null : this.toDespatchDate.toString();
        toRet.startedOrNot = this.startedOrNot.value;
        toRet.endedOrNot = this.endedOrNot.value;
        toRet.vehicleIds = this.vehicleIds == null ? null : this.vehicleIds.map(v => v.toString());
        toRet.journeyIds = this.journeyIds == null ? null : this.journeyIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredJourneyHeadersResult extends ModelObject<FilteredJourneyHeadersResult> {

    public matchingCount: number = 0;
    public journeyHeaders: (JourneyHeader)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.journeyHeaders = jsonSerializedObject.journeyHeaders.map((v: any) => new JourneyHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.journeyHeaders = this.journeyHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "journeyHeaders", rule: (v: any) => !!v || "journeyHeaders is required"},
            {field: "journeyHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as JourneyHeader[]).reduce((acc: Array<string>, curr: JourneyHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredJourneysResult extends ModelObject<FilteredJourneysResult> {

    public matchingCount: number = 0;
    public journeys: (Journey)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.journeys = jsonSerializedObject.journeys.map((v: any) => new Journey(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.journeys = this.journeys.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "journeys", rule: (v: any) => !!v || "journeys is required"},
            {field: "journeys", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Journey[]).reduce((acc: Array<string>, curr: Journey) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneyDetailCalculated extends ModelObject<JourneyDetailCalculated> {

    public numberOfStops: number = 0;
    public totalWeightKg: number = 0;
    public totalDurationSeconds: number = 0;
    public distanceKm: number = 0;
    public polyline: string = "";
    public stopTimeSeconds: number = 0;
    public travelTimeSeconds: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.numberOfStops = jsonSerializedObject.numberOfStops;
          this.totalWeightKg = jsonSerializedObject.totalWeightKg;
          this.totalDurationSeconds = jsonSerializedObject.totalDurationSeconds;
          this.distanceKm = jsonSerializedObject.distanceKm;
          this.polyline = jsonSerializedObject.polyline;
          this.stopTimeSeconds = jsonSerializedObject.stopTimeSeconds;
          this.travelTimeSeconds = jsonSerializedObject.travelTimeSeconds;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.numberOfStops = this.numberOfStops;
        toRet.totalWeightKg = this.totalWeightKg;
        toRet.totalDurationSeconds = this.totalDurationSeconds;
        toRet.distanceKm = this.distanceKm;
        toRet.polyline = this.polyline;
        toRet.stopTimeSeconds = this.stopTimeSeconds;
        toRet.travelTimeSeconds = this.travelTimeSeconds;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "numberOfStops", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfStops is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfStops", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfStops is above maximum allowed value: 2147483647"},
    ];
}


export class JourneyDetail extends ModelObject<JourneyDetail> {

    public journey: Journey = new Journey();
    public calculated: JourneyDetailCalculated = new JourneyDetailCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.journey = new Journey(jsonSerializedObject.journey);
          this.calculated = new JourneyDetailCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.journey = this.journey.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "journey", rule: (v: any) => !!v || "journey is required"},
            {field: "journey", rule: (v: any) => v.validate()},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class FilteredJourneDetailsResult extends ModelObject<FilteredJourneDetailsResult> {

    public matchingCount: number = 0;
    public details: (JourneyDetail)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.details = jsonSerializedObject.details.map((v: any) => new JourneyDetail(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.details = this.details.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as JourneyDetail[]).reduce((acc: Array<string>, curr: JourneyDetail) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class JourneyStop extends ModelObject<JourneyStop> {

    public stopKey: StopKey = new StopKey();
    public title: string = "";
    public addressBusinessName: string = "";
    public planningStatus: EnumJourneyStopPlanningStatus = EnumJourneyStopPlanningStatus.unknown;
    private _expectedSiteId: Guid | null = null;
    public get expectedSiteId(): Guid | null { return this._expectedSiteId }
    public set expectedSiteId(value: Guid | null) {
      this._expectedSiteId = value === undefined ? null : value;
    }
    public addressLine1: string = "";
    public addressLine2: string = "";
    public addressLine3: string = "";
    public addressLine4: string = "";
    public addressLine5: string = "";
    public addressLine6: string = "";
    public addressPostcode: string = "";
    private _deliveryAreaId: Guid | null = null;
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    public stopMinutes: number = 0;
    private _latitude: number | null = null;
    public get latitude(): number | null { return this._latitude }
    public set latitude(value: number | null) {
      this._latitude = value === undefined ? null : value;
    }
    private _longitude: number | null = null;
    public get longitude(): number | null { return this._longitude }
    public set longitude(value: number | null) {
      this._longitude = value === undefined ? null : value;
    }
    private _timeWindowStart: LocalTime | null = null;
    public get timeWindowStart(): LocalTime | null { return this._timeWindowStart }
    public set timeWindowStart(value: LocalTime | null) {
      this._timeWindowStart = value === undefined ? null : value;
    }
    private _timeWindowEnd: LocalTime | null = null;
    public get timeWindowEnd(): LocalTime | null { return this._timeWindowEnd }
    public set timeWindowEnd(value: LocalTime | null) {
      this._timeWindowEnd = value === undefined ? null : value;
    }
    public weightToDeliverKg: number = 0;
    public weightToCollectKg: number = 0;
    public outletCode: string = "";
    private _outletPreferredDeliveryStart: LocalTime | null = null;
    public get outletPreferredDeliveryStart(): LocalTime | null { return this._outletPreferredDeliveryStart }
    public set outletPreferredDeliveryStart(value: LocalTime | null) {
      this._outletPreferredDeliveryStart = value === undefined ? null : value;
    }
    private _outletPreferredDeliveryEnd: LocalTime | null = null;
    public get outletPreferredDeliveryEnd(): LocalTime | null { return this._outletPreferredDeliveryEnd }
    public set outletPreferredDeliveryEnd(value: LocalTime | null) {
      this._outletPreferredDeliveryEnd = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _journeyId: Guid | null = null;
    public get journeyId(): Guid | null { return this._journeyId }
    public set journeyId(value: Guid | null) {
      this._journeyId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stopKey = new StopKey(jsonSerializedObject.stopKey);
          this.title = jsonSerializedObject.title;
          this.addressBusinessName = jsonSerializedObject.addressBusinessName;
          this.planningStatus = EnumJourneyStopPlanningStatus.getByValue(jsonSerializedObject.planningStatus, this.planningStatus);
          this.expectedSiteId = jsonSerializedObject.expectedSiteId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.expectedSiteId);
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.addressPostcode = jsonSerializedObject.addressPostcode;
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.stopMinutes = jsonSerializedObject.stopMinutes;
          this.latitude = jsonSerializedObject.latitude == null ? null : jsonSerializedObject.latitude;
          this.longitude = jsonSerializedObject.longitude == null ? null : jsonSerializedObject.longitude;
          this.timeWindowStart = jsonSerializedObject.timeWindowStart == null ? null : LocalTime.parse(jsonSerializedObject.timeWindowStart);
          this.timeWindowEnd = jsonSerializedObject.timeWindowEnd == null ? null : LocalTime.parse(jsonSerializedObject.timeWindowEnd);
          this.weightToDeliverKg = jsonSerializedObject.weightToDeliverKg;
          this.weightToCollectKg = jsonSerializedObject.weightToCollectKg;
          this.outletCode = jsonSerializedObject.outletCode;
          this.outletPreferredDeliveryStart = jsonSerializedObject.outletPreferredDeliveryStart == null ? null : LocalTime.parse(jsonSerializedObject.outletPreferredDeliveryStart);
          this.outletPreferredDeliveryEnd = jsonSerializedObject.outletPreferredDeliveryEnd == null ? null : LocalTime.parse(jsonSerializedObject.outletPreferredDeliveryEnd);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.journeyId = jsonSerializedObject.journeyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.journeyId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stopKey = this.stopKey.toJsonSerializedObject();
        toRet.title = this.title;
        toRet.addressBusinessName = this.addressBusinessName;
        toRet.planningStatus = this.planningStatus.value;
        toRet.expectedSiteId = this.expectedSiteId == null ? null : this.expectedSiteId.toString();
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.addressPostcode = this.addressPostcode;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.stopMinutes = this.stopMinutes;
        toRet.latitude = this.latitude == null ? null : this.latitude;
        toRet.longitude = this.longitude == null ? null : this.longitude;
        toRet.timeWindowStart = this.timeWindowStart == null ? null : this.timeWindowStart.toString();
        toRet.timeWindowEnd = this.timeWindowEnd == null ? null : this.timeWindowEnd.toString();
        toRet.weightToDeliverKg = this.weightToDeliverKg;
        toRet.weightToCollectKg = this.weightToCollectKg;
        toRet.outletCode = this.outletCode;
        toRet.outletPreferredDeliveryStart = this.outletPreferredDeliveryStart == null ? null : this.outletPreferredDeliveryStart.toString();
        toRet.outletPreferredDeliveryEnd = this.outletPreferredDeliveryEnd == null ? null : this.outletPreferredDeliveryEnd.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.journeyId = this.journeyId == null ? null : this.journeyId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stopKey", rule: (v: any) => !!v || "stopKey is required"},
            {field: "stopKey", rule: (v: any) => v.validate()},
            {field: "expectedSiteId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyStop.ExpectedSiteId cannot be empty"},
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyStop.DeliveryAreaId cannot be empty"},
            {field: "stopMinutes", rule: (v: any) => v == null || v >= -2147483648 || "StopMinutes is not greater than minimum allowed value: -2147483648"},
            {field: "stopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "StopMinutes is above maximum allowed value: 2147483647"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyStop.OutletId cannot be empty"},
            {field: "journeyId", rule: (v: any) => v == null || !v.isEmpty() || "JourneyStop.JourneyId cannot be empty"},
    ];
}


export class JourneyService extends ServiceBase {

  public async getJourney(journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Journey> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = (response:any) => new Journey(response.journey);;
    return this.makeJsonRequest("journey/v3/getJourney", req, false, extractor, opts);
  };

  public async getJourneys(journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Journey)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.journeys.map((v: any) => new Journey(v));;
    return this.makeJsonRequest("journey/v3/getJourneys", req, false, extractor, opts);
  };

  public async getJourneyHeaders(journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(JourneyHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.journeyHeaders.map((v: any) => new JourneyHeader(v));;
    return this.makeJsonRequest("journey/v3/getJourneyHeaders", req, false, extractor, opts);
  };

  public async savePlannedJourney(journey: Journey, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journey:journey.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/savePlannedJourney", req, true, extractor, opts);
  };

  public async savePlannedJourneys(journeys: readonly Journey[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeys:journeys.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/savePlannedJourneys", req, true, extractor, opts);
  };

  public async hardDeletePlannedJourney(journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/hardDeletePlannedJourney", req, true, extractor, opts);
  };

  public async hardDeletePlannedJourneys(journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/hardDeletePlannedJourneys", req, true, extractor, opts);
  };

  public async getRecentCompletedJourneyHeadersForVehicle(vehicleId: Guid, maxResults: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(JourneyHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicleId:vehicleId.toString(),
    maxResults:maxResults,
    };
    const extractor = (response:any) => response.journeyHeaders.map((v: any) => new JourneyHeader(v));;
    return this.makeJsonRequest("journey/v3/getRecentCompletedJourneyHeadersForVehicle", req, false, extractor, opts);
  };

  public async getJourneyHeadersByFilter(filter: JourneyFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredJourneyHeadersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredJourneyHeadersResult(response.results);;
    return this.makeJsonRequest("journey/v3/getJourneyHeadersByFilter", req, false, extractor, opts);
  };

  public async getJourneysByFilter(filter: JourneyFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredJourneysResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredJourneysResult(response.results);;
    return this.makeJsonRequest("journey/v3/getJourneysByFilter", req, false, extractor, opts);
  };

  public async getJourneyDetail(journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<JourneyDetail> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = (response:any) => new JourneyDetail(response.detail);;
    return this.makeJsonRequest("journey/v3/getJourneyDetail", req, false, extractor, opts);
  };

  public async getJourneyDetails(journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(JourneyDetail)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.details.map((v: any) => new JourneyDetail(v));;
    return this.makeJsonRequest("journey/v3/getJourneyDetails", req, false, extractor, opts);
  };

  public async getJourneyDetailsByFilter(filter: JourneyFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredJourneDetailsResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredJourneDetailsResult(response.results);;
    return this.makeJsonRequest("journey/v3/getJourneyDetailsByFilter", req, false, extractor, opts);
  };

  public async getJourneyStopsForDate(despatchDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(JourneyStop)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    despatchDate:despatchDate.toString(),
    };
    const extractor = (response:any) => response.stops.map((v: any) => new JourneyStop(v));;
    return this.makeJsonRequest("journey/v3/getJourneyStopsForDate", req, false, extractor, opts);
  };

  public async getJourneyStopsByKeys(stopKeys: readonly StopKey[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(JourneyStop)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stopKeys:stopKeys.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.stops.map((v: any) => new JourneyStop(v));;
    return this.makeJsonRequest("journey/v3/getJourneyStopsByKeys", req, false, extractor, opts);
  };

  public async saveVehicleCheck(journeyId: Guid, startOdometerReading: number | null, endOdometerReading: number | null, vehicleCheckLines: readonly JourneyVehicleCheckLine[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    startOdometerReading:startOdometerReading == null ? null : startOdometerReading,
    endOdometerReading:endOdometerReading == null ? null : endOdometerReading,
    vehicleCheckLines:vehicleCheckLines == null ? null : vehicleCheckLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/saveVehicleCheck", req, true, extractor, opts);
  };

  public async startJourney(journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/startJourney", req, true, extractor, opts);
  };

  public async abortJourney(journeyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/abortJourney", req, true, extractor, opts);
  };

  public async endJourney(journeyId: Guid, endOdometerReading: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    journeyId:journeyId.toString(),
    endOdometerReading:endOdometerReading,
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/endJourney", req, true, extractor, opts);
  };

  public async setOrderOfJourneysOnDay(vehicleId: Guid, despatchDate: LocalDate, journeyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicleId:vehicleId.toString(),
    despatchDate:despatchDate.toString(),
    journeyIds:journeyIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("journey/v3/setOrderOfJourneysOnDay", req, true, extractor, opts);
  };
}

export const journeyService = new JourneyService();